<script>
import lodash from 'lodash';

export default {
  props: {
    data: Object,
    activRows: {
      type: Boolean,
      default: false,
    },
    sorted: {
      type: Boolean,
      default: true,
    },
    fetching: {
      type: Boolean,
      default: false,
    },
    moreButtonText: {
      type: String,
    },
    totalCount: {
      type: Number,
    },
    pageStore: {
      type: Number,
    },
    pageSizeDefault: {
      type: Number,
    },
    pageCustom: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortByCol: undefined,
      sortDirection: undefined,
      selected: undefined,
      sortByStart: undefined,
      page: 1,
      pageSize: undefined,
      pageSizeOptions: [10, 20, 50, 100, 300],
      maxLength: 45,
    };
  },
  created() {
    this.pageSizeAssignment(this.data);
    if (this.pageCustom) {
      this.pageSizeOptions = [10, 20, 50, 100];
    }
  },
  computed: {
    sortedRows() {
      let { sortBy } = this.data;
      this.sortByStart = sortBy;
      if (this.sortByCol || this.sortByCol === null) {
        sortBy = this.sortByCol;
        this.sortByStart = undefined;
      }
      const { rows, cols, sortDirection } = this.data;
      let result = rows;
      if (sortBy) {
        const sortFn = sortBy && cols[lodash.findIndex(cols, { name: sortBy })].sortFn;
        result = lodash.sortBy(rows, sortFn
          || ((row) => (typeof row[sortBy] === 'string' ? row[sortBy].toLowerCase() : row[sortBy])));
        if (!this.sortDirection) {
          this.sortDirection = sortDirection || 'Asc';
        }
        if (this.sortDirection === 'Desc') result.reverse();
        this.$emit('sort', sortBy, this.sortDirection);
      }
      return result;
    },
    more() {
      const page = this.pageStore || this.page;
      if (this.totalCount) {
        let lastPage = false;
        if ((this.totalCount % this.pageSize === 0
          && page === this.totalCount / this.pageSize)
          || (this.totalCount % this.pageSize !== 0
          && page === Math.round(this.totalCount / this.pageSize) + 1)) {
          lastPage = true;
        }

        return !lastPage && this.data.rows.length !== this.totalCount;
      }
      return false;
    },
    moreShow() {
      return this.more && !this.fetching && this.data.rows && (this.data.rows.length > 0);
    },
  },
  watch: {
    data: {
      handler(val) {
        if (val.rows && this.pageSize === undefined) {
          if (val.rows.length === this.totalCount
            && this.page === 1) this.pageSize = this.totalCount;
          if (val.rows.length < this.totalCount
            && this.page === 1) this.pageSize = val.rows.length;
        }
        this.pageSizeAssignment(val);
      },
      deep: true,
    },
    sortByCol() {
      this.sortDirection = 'Asc';
    },
    pageSize(value, oldValue) {
      if (this.pageSizeOptions.indexOf(value) === -1) {
        this.pageSizeOptions.push(value);
        this.pageSizeOptions = lodash.orderBy(this.pageSizeOptions);
      }
      if ((value !== this.pageSizeDefault || value !== oldValue) && oldValue !== undefined) {
        this.refreshPage();
      }
    },
  },
  methods: {
    sort(name, unsortable) {
      if (unsortable || ((this.sortByCol === name || this.sortByStart === name) && this.sortDirection === 'Desc')) {
        this.sortByCol = null;
      } else if ((this.sortByCol === name || this.sortByStart === name) && this.sortDirection === 'Asc') {
        this.sortDirection = 'Desc';
      } else {
        this.sortByCol = name;
      }
    },
    onClickRow(activeRow, index) {
      this.selected = index;
      this.$emit('clickRow', activeRow);
    },
    moreResults() {
      this.page += 1;
      this.$emit('moreResults', { page: this.page, pageSize: this.pageSize });
    },
    pageSizeAssignment(val) {
      if (val.rows && this.pageSize === undefined) {
        if (this.pageSizeDefault) {
          this.pageSize = this.pageSizeDefault;
        } else {
          if (val.rows.length === this.totalCount
            && this.page === 1) this.pageSize = this.totalCount;
          if (val.rows.length < this.totalCount
            && this.page === 1) this.pageSize = val.rows.length;
        }
      }
    },
    refreshPage() {
      this.$emit('refreshPage', this.pageSize);
    },
    truncateLabel(label) {
      if (label && label.length > this.maxLength) {
        return `${label.substring(0, this.maxLength - 3)}...`;
      }
      return label;
    },
  },
};
</script>

<template lang="pug">
- var typeClass = "{[$style.typeNumber]: col.type === Number}";
div(:class="$style.wrap")
  v-select(
    :class="[$style.select, pageCustom && $style.selectCustom]"
    v-if="pageSizeDefault"
    :items="pageSizeOptions"
    v-model="pageSize"
    :label="$t('T_PAGE_SIZE')")
  table(:class="$style.root")
    thead
      tr
        th(v-for="col in data.cols", :class="[$style.header, col.classHeader, " + typeClass + "]", :title="col.title")
          div(:class="$scopedSlots[col.label] && $style.wrapHeaderContent")
            -
              var classLabel = "[$style.label, (sortByCol === col.name || sortByStart === col.name)"
                + " && $style.labelActive, (!col.unsortable || sorted) && $style.labelSortable]";
            span(v-if="col.label", :data-test="col.dataTestHeader",
              :class=(classLabel),
              @click="sort(col.name, col.unsortable || !sorted)")
              span(:class="$style.labelText", :title="col.title") {{ truncateLabel(col.label) }}
              -
                var classIcon="[$style.labelSort, (sortByCol === col.name || sortByStart === col.name)"
                  + " && $style['labelSort' + sortDirection]]";
              ui-icon(:class=(classIcon)) dropdown
            slot(v-if="$scopedSlots[col.label]", :name="col.label")

    transition-group(is="tbody", name="row-transition", :move-class="$style.rowMove", tag="tbody")
      tr(v-for="(row, index) in sortedRows",
        :class="[$style.rowData, activRows && $style.activRow, activRows && index === selected && $style.selectedRow]",
        :style="{ backgroundColor: row.messages === 0 ? '#ffefd5' : '' }",
        :key="`row-${row.uniqueKey || index}`",
        @click="onClickRow(row, index)")
        td(v-for="col in data.cols",
          :data-test="col.dataTestData",
          :class="[$style.data, col.classData, " + typeClass + "]")
          slot(v-if="$scopedSlots[col.name]", :name="col.name", :value="row[col.name]", :data="row")
          template(v-else)
            template(v-if="col.type === Number") {{ row[col.name] ? row[col.name].toFixed(col.digits) : '0' }}
            template(v-if="col.type === Date") {{ row[col.name] ? row[col.name].toLocaleString() : 'no data' }}
            template(v-if="col.type === String") {{ row[col.name] }}
  ui-button(
    v-if="moreShow"
    icon="expand_more",
    colored="primary",
    type="iconText"
    :class="$style.moreButton",
    @click.native="moreResults()",
    @keyup.enter.native="moreResults()") {{ moreButtonText || $t('T_MORE') }}
  v-progress-circular(
    v-if="fetching"
    indeterminate
    class="primary--text"
    :class="$style.moreButton")
</template>

<style module lang="sass">
.wrap
  width: 100%
  position: relative
  display: flex
  flex-direction: column
.root
  width: 100%
  table-layout: auto
  border-spacing: 0
.header
  font-size: 12px
  color: $color_text_second
  font-weight: 400
  line-height: 16px
  text-align: left
  height: 32px
  padding: 8px 16px
  vertical-align: top
  user-select: none
.wrapHeaderContent
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between
  align-items: flex-start
  align-content: space-around
.data
  vertical-align: top
  padding: 16px
  line-height: 24px
  font-size: 16px
  background: $color_transparent
  transition: background $a_speed
  position: relative
  transition: all $a_speed
.type
  &Number
    text-align: center
    .label
      flex-direction: row-reverse
      &Sort
        margin: 0 4px 0 -16px
.rowMove
  box-shadow: $sh_dp4
  background: #fff
  // _: -ms-fullscreen, :root & // TODO: IE11
  //  box-shadow: none

.rowData
  transition: all $a_speed
  & + &
    .data
      min-width: 100px
      border-top: 1px solid $color_divider
  &:hover .data
    background: $color_transparent_bg_dark
  &:hover .data, &:hover + & .data
    border-top-color: $color_transparent_bg_dark_second
.activRow
  cursor: pointer
.selectedRow
  background: $color_transparent_bg_dark_second
.label
  white-space: nowrap
  display: inline-flex
  justify-content: center
  transition: color $a_speed
  &:hover
    color: $color_text
  &Active
    font-weight: 500
    color: $color_text
  &Text
    white-space: normal
  &Sortable
    cursor: pointer
  &Sort
    height: 16px
    width: 16px
    margin: 0 -19px 0 4px
    opacity: 0
    vertical-align: top
    display: inline-block
    color: inherit
    transform: rotate(90deg)
    transition: transform $a_speed, opacity $a_speed
    &Asc
      transform: rotate(180deg)
      opacity: 1
    &Desc
      transform: rotate(0deg)
      opacity: 1
.moreButton
  display: flex
  align-self: center
  margin: 16px 0

@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)
  .label
    max-width: 100%

@-moz-document url-prefix()
  .header
    position: relative
  .wrapHeaderContent
    height: auto
    padding-bottom: 40px

.moreButton
  display: flex
  align-self: center
  margin: 16px 0
.select
  margin-left: 32px
  min-width: 200px
  max-width: 200px
  padding-bottom: 0
  min-height: 70px
  &Custom
    margin-left: 0
.row-transition-enter-active, .row-transition-leave-active
  transition: opacity 0.5s ease, transform 0.5s ease

.row-transition-enter, .row-transition-leave-to
  opacity: 0
  transform: translateY(20px)

</style>
